/**
 * This is the ProductInformation component.
 *
 * @module views/Organisms/ProductInformation
 */

import './ProductInformation.scss';

import React, { useEffect, useRef } from 'react';

import Cookies from 'js-cookie';
import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Link from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { devLogger } from '@ulta/core/utils/devMode/devMode';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import ReviewStars from '@ulta/components/ReviewStars/ReviewStars';

/**
 * Represents a ProductInformation component
 *
 * @method
 * @param {ProductInformationProps} props - React properties passed from composition
 * @returns ProductInformation
 */
export const ProductInformation = ( props ) => {
  const {
    brandAction,
    productName,
    productId,
    rating,
    ratingAction,
    reviewCount,
    reviewsCountLabel,
    badges,
    writeReviewAction,
    reviewLink,
    askQuestionAction,
    qAndAAction,
    reviewAccessibilityLabel,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold,
    id,
    meta,
    socialTags
  } = props;

  const showSingleBadge = hasItems( badges ) && badges.length === 1;
  const showMultipleBadges = hasItems( badges ) && badges.length > 1;

  // Start Datacapture Impression
  const componentRef = useRef();
  useIntersectionObserver(
    componentRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );
  // End Datacapture Impression

  // useEffect for triggering SocialTags TrackingBeacons
  const { user } = useUserContext();
  useEffect( () => {
    if( hasItems( meta?.trackingBeacons?.items ) ){
      meta.trackingBeacons.items.map( async( item ) => {
        try {
          var url = new URL( item.beaconURL );
          const urlParams = new URLSearchParams( url.search );
          urlParams.append( 'gti', user.gti );
          const siteCookie = Cookies.get( 'X_ULTA_SITE' );
          if( Cookies.get( 'DEBUG' ) && siteCookie ){
            urlParams.append( 'debug-header-ULTASITE', siteCookie );
          }
          url.search = urlParams;
          const beaconURL = url.toString();
          const headers = Object.fromEntries( item.headers?.map( header => [header.key, header.value] ) );
          await fetch( beaconURL, {
            method: item.method,
            headers: headers
          } );
        }
        catch ( error ){
          devLogger( { title: 'SocialTags TrackingBeacon Error', value: error } );
        }
      } );
    }
  }, [] );

  return (
    <div className='ProductInformation'
      id={ id }
      ref={ componentRef }
    >
      <Text htmlTag='h1'>
        <Text htmlTag='span'>
          <Link
            compact
            url={ brandAction?.url }
            action={ brandAction }
          >
            { brandAction?.label }
          </Link>
          &nbsp;
        </Text>
        <Text
          htmlTag='span'
          textStyle='title-5'
        >
          { productName }
        </Text>
      </Text>
      <ReviewStars size='s'
        rating={ rating }
        ratingAction={ ratingAction }
        reviews={ reviewCount }
        reviewsCountLabel={ reviewsCountLabel }
        reviewable={ true }
        reviewLink={ reviewLink }
        writeReviewAction={ writeReviewAction }
        askQuestionAction={ askQuestionAction }
        qAndAAction={ qAndAAction }
        reviewAccessibilityLabel={ reviewAccessibilityLabel }
      />
      { showSingleBadge &&
        <Tag
          variant='default'
          style='default'
          theme='default'
          message={ badges[0] }
        />
      }
      { showMultipleBadges &&
        <ul className='ProductInformation__badges'>
          { badges.map( ( badge, index ) => (
            <li key={ index }>
              <Tag
                variant='default'
                style='default'
                theme='default'
                message={ badge }
              />
            </li>
          ) ) }
        </ul>
      }
      { socialTags &&
        <AsyncComponent
          moduleName='SocialTags'
          productId={ productId }
          id={ socialTags.id }
          { ...spreadProps( { ...socialTags } ) }
        />
      }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ProductInformationProps
 * @type {object}
 * @property {string} description - Sets the description string
 * @property {object} brandAction - This is the brandAction
 * @property {string} productName - Sets the product name string for product
 * @property {string} productId - Sets the product id
 * @property {number} rating - Sets the rating info for star rating component
 * @property {object} ratingAction - This is the ratingAction for product
 * @property {number} reviewCount - Sets the rating info for star rating component
 * @property {string} reviewsCountLabel - Sets the reviews label for star rating component
 * @property {array} badges - List of badges
 * @property {string} itemLabel  - Label for item name displayed
 * @property {boolean} reviewable - Flag for enabling review link
 * @property {string} reviewLink - Sets is the link for review
 * @property {object} writeReviewAction - This is the writeReviewAction
 * @property {object} askQuestionAction - This is askQuestionAction which have question link and label
 * @property {object} qAndAAction - A action which have Question and Answer link and label
 * @property {string} reviewAccessibilityLabel - Sets the label for review accessibility
 * @property {string} id - Sets the id string
 * @property {object} meta - Sets the meta data object, TrackingBeacons etc.
 * @property {object} socialTags - Sets the SocialTags object
 */
export const propTypes = {
  /** This is the brandAction for product */
  brandAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This is the product name for product */
  productName: PropTypes.string,
  /** This is the product id for product */
  productId: PropTypes.string,
  /** This is the rating info for product */
  rating: PropTypes.number,
  /** This is  ratingAction which have rating link and label*/
  ratingAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This is the review count for product */
  reviewCount: PropTypes.number,
  /** This is the review count label for product */
  reviewsCountLabel: PropTypes.string,
  /** This is the list of badge info for product */
  badges: PropTypes.array,
  /** This is the itemLabel for product */
  itemLabel: PropTypes.string,
  /** This is the flag for enabling review link */
  reviewable: PropTypes.bool,
  /** This is the link for review */
  reviewLink: PropTypes.string,
  /** This is the writeReviewAction which have review link and label */
  writeReviewAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This is the askQuestionAction which have question link and label */
  askQuestionAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This is the qAndAAction which have link and label */
  qAndAAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string
  } ),
  /** This is the label for review accessibility */
  reviewAccessibilityLabel: PropTypes.string,
  /** This is the id for product information */
  id: PropTypes.string,
  /** This is the meta object, currently for use with TrackingBeacons */
  meta: PropTypes.object,
  /** This is the SocialTags object */
  socialTags: PropTypes.object
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} rootMargin='0px' - Margin around the root.
 * @property {object} root=null - The element that is used as the viewport for checking visiblity of the target.
 * @property {number} threshold=[0, 0.25, 0.5, 0.75, 1] - Either a single number or an array of numbers which indicate at what percentage of the target's visibility the observer's callback should be executed.
*/
export const defaultProps = {
  rootMargin: '0px',
  root: null,
  threshold: [0, 0.25, 0.5, 0.75, 1]
};

ProductInformation.propTypes = propTypes;
ProductInformation.defaultProps = defaultProps;

export default ProductInformation;
